// franklycatering #1557 — the Events filters: state, matching, labels, quick
// views, the summary line, List sorting, the closed-events date window and
// the saved view. No React, so every rule is unit-tested.

import { addDays, endOfMonth, format, startOfMonth } from "date-fns";
import {
  CLOSED_STAGES,
  EVENT_STAGES,
  EVENT_STAGE_LABELS,
  OPEN_STAGES,
  round2,
  type EventStage,
  type EventsView,
} from "@/lib/event-pipeline";
import {
  NO_REPLY_DAYS,
  QUOTE_STATUS_KEYS,
  QUOTE_STATUS_KEY_LABELS,
  type BoardRow,
  type QuoteStatusKey,
} from "@/lib/event-board";
import { SERVICE_TYPES, SERVICE_TYPE_SHORT_LABELS, type ServiceType } from "@/types/quotes";

export type WhenOption = "upcoming" | "next7" | "next30" | "month" | "past" | "all" | "custom";

export const WHEN_OPTIONS: { key: WhenOption; label: string }[] = [
  { key: "upcoming", label: "Upcoming" },
  { key: "next7", label: "Next 7 days" },
  { key: "next30", label: "Next 30 days" },
  { key: "month", label: "This month" },
  { key: "past", label: "Past" },
  { key: "all", label: "All dates" },
  { key: "custom", label: "Custom…" },
];

export interface EventFilters {
  when: WhenOption;
  /** Custom range, yyyy-mm-dd, inclusive. */
  from: string;
  to: string;
  services: ServiceType[];
  stages: EventStage[];
  quoteStatuses: QuoteStatusKey[];
  /** The "Leads with no reply in 3+ days" quick view's extra condition. */
  noReply: boolean;
  /** ?incomplete=1: partial leads are loaded too. Never saved. */
  incomplete: boolean;
}

export type MultiFilterKey = "services" | "stages" | "quoteStatuses";

const ALL_STAGES: EventStage[] = [...OPEN_STAGES, ...CLOSED_STAGES];
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** yyyy-mm-dd for a local date. */
const isoDate = (d: Date) => format(d, "yyyy-MM-dd");

export function defaultFilters(today: Date): EventFilters {
  return {
    when: "upcoming",
    from: isoDate(today),
    to: isoDate(addDays(today, 30)),
    services: [...SERVICE_TYPES],
    stages: [...OPEN_STAGES],
    quoteStatuses: [...QUOTE_STATUS_KEYS],
    noReply: false,
    incomplete: false,
  };
}

/** ?incomplete=1 (the Quotes page's "unfinished leads" link): every date and stage, partial leads in. */
export function incompleteFilters(today: Date): EventFilters {
  return { ...defaultFilters(today), when: "all", stages: [...ALL_STAGES], incomplete: true };
}

/** Same members, ignoring order. */
export const sameSet = <T>(a: readonly T[], b: readonly T[]) => a.length === b.length && a.every((x) => b.includes(x));

export function isDefaultFilters(f: EventFilters, today: Date): boolean {
  const d = defaultFilters(today);
  return (
    f.when === d.when &&
    sameSet(f.services, d.services) &&
    sameSet(f.stages, d.stages) &&
    sameSet(f.quoteStatuses, d.quoteStatuses) &&
    !f.noReply &&
    !f.incomplete
  );
}

function validIso(s: unknown): s is string {
  return typeof s === "string" && ISO_DATE.test(s) && !Number.isNaN(Date.parse(s));
}

/** The When filter. Upcoming takes undated events and every Post-event one. */
export function matchesWhen(row: Pick<BoardRow, "date" | "daysOut" | "stage">, f: EventFilters, today: Date): boolean {
  const n = row.daysOut;
  switch (f.when) {
    case "upcoming":
      return n === null || n >= 0 || row.stage === "post_event";
    case "next7":
      return n !== null && n >= 0 && n <= 7;
    case "next30":
      return n !== null && n >= 0 && n <= 30;
    case "month":
      return !!row.date && row.date.slice(0, 7) === isoDate(today).slice(0, 7);
    case "past":
      return n !== null && n < 0;
    case "custom":
      return (
        !!row.date && (!validIso(f.from) || row.date >= f.from) && (!validIso(f.to) || row.date <= f.to)
      );
    default:
      return true;
  }
}

export function matchesFilters(row: BoardRow, f: EventFilters, today: Date): boolean {
  if (!f.stages.includes(row.stage)) return false;
  if (!f.services.includes(row.service)) return false;
  if (!f.quoteStatuses.includes(row.quoteStatus)) return false;
  if (f.noReply && !(row.noReplyDays !== null && row.noReplyDays >= NO_REPLY_DAYS)) return false;
  return matchesWhen(row, f, today);
}

/** How many rows match with `key` narrowed to just `value` (the count beside each option). */
export function countWith<K extends MultiFilterKey>(
  rows: BoardRow[],
  f: EventFilters,
  key: K,
  value: EventFilters[K][number],
  today: Date,
): number {
  const narrowed = { ...f, [key]: [value] } as EventFilters;
  return rows.filter((r) => matchesFilters(r, narrowed, today)).length;
}

/** Adds or removes one option, keeping the canonical order. */
export function toggleOption<T extends string>(list: readonly T[], value: T, order: readonly T[]): T[] {
  const next = list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
  return order.filter((x) => next.includes(x));
}

export interface FilterOption<T extends string> {
  key: T;
  label: string;
}

export const SERVICE_OPTIONS: FilterOption<ServiceType>[] = SERVICE_TYPES.map((key) => ({
  key,
  label: SERVICE_TYPE_SHORT_LABELS[key],
}));
export const QUOTE_STATUS_OPTIONS: FilterOption<QuoteStatusKey>[] = QUOTE_STATUS_KEYS.map((key) => ({
  key,
  label: QUOTE_STATUS_KEY_LABELS[key],
}));
export const STAGE_OPTION_GROUPS: { label: "Open" | "Closed"; options: FilterOption<EventStage>[] }[] = [
  { label: "Open", options: OPEN_STAGES.map((key) => ({ key, label: EVENT_STAGE_LABELS[key] })) },
  { label: "Closed", options: CLOSED_STAGES.map((key) => ({ key, label: EVENT_STAGE_LABELS[key] })) },
];
/** Event status shortcuts: "Open only", "Closed only", "All". */
export const STAGE_SHORTCUTS: { label: string; stages: EventStage[] }[] = [
  { label: "Open only", stages: OPEN_STAGES },
  { label: "Closed only", stages: CLOSED_STAGES },
  { label: "All", stages: ALL_STAGES },
];
export const ALL_SERVICES: ServiceType[] = [...SERVICE_TYPES];
export const ALL_QUOTE_STATUSES: QuoteStatusKey[] = [...QUOTE_STATUS_KEYS];
export const ALL_EVENT_STAGES: EventStage[] = ALL_STAGES;

function pickLabel<T extends string>(picked: readonly T[], all: readonly T[], labels: Record<T, string>): string {
  if (sameSet(picked, all)) return "All";
  if (picked.length === 0) return "None";
  const first = all.find((x) => picked.includes(x)) ?? picked[0];
  return labels[first] + (picked.length > 1 ? ` +${picked.length - 1}` : "");
}

/** The value after "Service:". */
export function serviceFilterLabel(services: readonly ServiceType[]): string {
  return pickLabel(services, SERVICE_TYPES, SERVICE_TYPE_SHORT_LABELS);
}

/** The value after "Quote status:". */
export function quoteStatusFilterLabel(statuses: readonly QuoteStatusKey[]): string {
  return pickLabel(statuses, QUOTE_STATUS_KEYS, QUOTE_STATUS_KEY_LABELS);
}

/** The value after "Event status:". */
export function stageFilterLabel(stages: readonly EventStage[]): string {
  if (sameSet(stages, OPEN_STAGES)) return "Open";
  if (sameSet(stages, ALL_STAGES)) return "All";
  if (sameSet(stages, CLOSED_STAGES)) return "Closed";
  if (stages.length === 1) return EVENT_STAGE_LABELS[stages[0]];
  return `${stages.length} stages`;
}

export type QuickViewKey = "week" | "waiting" | "quiet";

export interface QuickView {
  key: QuickViewKey;
  label: string;
  filters: (today: Date) => EventFilters;
  /** Needs the contacts read; disabled when it failed. */
  needsMessages?: boolean;
}

export const QUICK_VIEWS: QuickView[] = [
  {
    key: "week",
    label: "This week's events",
    filters: (today) => ({
      ...defaultFilters(today),
      when: "next7",
      stages: ["accepted", "booked", "final_details", "event_day"],
    }),
  },
  {
    key: "waiting",
    label: "Quotes waiting on the customer",
    filters: (today) => ({ ...defaultFilters(today), when: "all", stages: ["quoting"], quoteStatuses: ["sent"] }),
  },
  {
    key: "quiet",
    label: "Leads with no reply in 3+ days",
    filters: (today) => ({ ...defaultFilters(today), when: "all", stages: ["lead", "quoting"], noReply: true }),
    needsMessages: true,
  },
];

export function quickViewCount(rows: BoardRow[], view: QuickView, today: Date): number {
  const f = view.filters(today);
  return rows.filter((r) => matchesFilters(r, f, today)).length;
}

export interface BoardSummary {
  events: number;
  guests: number;
  /** Sum of each event's primary quote total. */
  quoted: number;
  /** Still owing on accepted (committed) quotes. */
  owing: number;
}

export function summarize(rows: BoardRow[]): BoardSummary {
  return {
    events: rows.length,
    guests: rows.reduce((s, r) => s + r.guests, 0),
    quoted: round2(rows.reduce((s, r) => s + r.total, 0)),
    owing: round2(rows.reduce((s, r) => s + r.owing, 0)),
  };
}

export type SortKey = "date" | "total";
export interface RowSort {
  key: SortKey;
  dir: 1 | -1;
}

/** List order. By date, undated events always come last. */
export function sortRows(rows: BoardRow[], sort: RowSort): BoardRow[] {
  return [...rows].sort((a, b) => {
    if (sort.key === "total") return (a.total - b.total) * sort.dir;
    if (!a.date || !b.date) return a.date ? -1 : b.date ? 1 : 0;
    return a.date.localeCompare(b.date) * sort.dir;
  });
}

/** The When filter's dates: what the closed (done, lost, cancelled) events are loaded for, and what the open-events cap is checked against. */
export interface DateWindow {
  from: string | null;
  to: string | null;
  /** Undated events count as upcoming. */
  includeUndated: boolean;
}

export function closedWindow(f: Pick<EventFilters, "when" | "from" | "to">, today: Date): DateWindow {
  const t = isoDate(today);
  switch (f.when) {
    case "upcoming":
      return { from: t, to: null, includeUndated: true };
    case "next7":
      return { from: t, to: isoDate(addDays(today, 7)), includeUndated: false };
    case "next30":
      return { from: t, to: isoDate(addDays(today, 30)), includeUndated: false };
    case "month":
      return {
        from: isoDate(startOfMonth(today)),
        to: isoDate(endOfMonth(today)),
        includeUndated: false,
      };
    case "past":
      return { from: null, to: isoDate(addDays(today, -1)), includeUndated: false };
    case "custom":
      return { from: validIso(f.from) ? f.from : null, to: validIso(f.to) ? f.to : null, includeUndated: false };
    default:
      return { from: null, to: null, includeUndated: true };
  }
}

/**
 * Whether the open-events cap can hide rows from this view (#1557). The open
 * read is soonest first with undated last, so the rows it leaves out are dated
 * on or after `boundary`, or undated.
 */
export function openCapMayHide(
  capped: boolean,
  boundary: string | null,
  window: DateWindow,
  stages: EventStage[],
): boolean {
  if (!capped || !stages.some((s) => OPEN_STAGES.includes(s))) return false;
  if (window.includeUndated) return true;
  if (boundary === null) return false; // every dated open event is loaded
  return window.to === null || window.to >= boundary;
}

const STORAGE_PREFIX = "tc-events-view-v1";

/** One saved view per person on this device. */
export function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}:${userId}`;
}

export interface StoredView {
  view: EventsView;
  when: WhenOption;
  from: string;
  to: string;
  services: ServiceType[];
  stages: EventStage[];
  quoteStatuses: QuoteStatusKey[];
}

export function toStoredView(view: EventsView, f: EventFilters): StoredView {
  return {
    view,
    when: f.when,
    from: f.from,
    to: f.to,
    services: f.services,
    stages: f.stages,
    quoteStatuses: f.quoteStatuses,
  };
}

/** Known values only; a list whose every value is unknown falls back. An empty list is a real choice. */
function pickList<T extends string>(raw: unknown, allowed: readonly T[], fallback: T[]): T[] {
  if (!Array.isArray(raw)) return fallback;
  const kept = allowed.filter((x) => raw.includes(x));
  return kept.length === 0 && raw.length > 0 ? fallback : kept;
}

/**
 * The saved view, each field checked on its own: an unknown or malformed field
 * takes its default and the rest are kept. Null when nothing usable is saved.
 */
export function parseStoredView(raw: string | null, today: Date): { view: EventsView; filters: EventFilters } | null {
  if (!raw) return null;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!v || typeof v !== "object" || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const d = defaultFilters(today);
  const when = WHEN_OPTIONS.some((w) => w.key === o.when) ? (o.when as WhenOption) : d.when;
  return {
    view: o.view === "list" ? "list" : "pipeline",
    filters: {
      ...d,
      when,
      from: validIso(o.from) ? o.from : d.from,
      to: validIso(o.to) ? o.to : d.to,
      services: pickList(o.services, SERVICE_TYPES, d.services),
      stages: pickList(o.stages, EVENT_STAGES, d.stages),
      quoteStatuses: pickList(o.quoteStatuses, QUOTE_STATUS_KEYS, d.quoteStatuses),
    },
  };
}
